import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WriteOffItemInputDto } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';

import { TabWriteoffDialogData, TabWriteoffDialogResult, WriteOffLineItem } from '@posModels';
import { isPartlyPaid, writeOffAmountCents } from '@posUtils';
import { SharedModule } from '@shared';

@Component({
  selector: 'app-tab-writeoff-dialog',
  standalone: true,
  imports: [ DecimalPipe, FormsModule, MatDialogModule, MatProgressSpinnerModule, TranslateModule, SharedModule ],
  templateUrl: './tab-writeoff-dialog.component.html',
  styleUrl: './tab-writeoff-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabWriteoffDialogComponent {
  private dialogRef = inject(MatDialogRef<TabWriteoffDialogComponent, TabWriteoffDialogResult>);
  private data = inject<TabWriteoffDialogData>(MAT_DIALOG_DATA);

  tabName = this.data.tabName;

  readonly isPartlyPaid = isPartlyPaid;

  lineItems = signal<WriteOffLineItem[]>(
    this.data.items
      .filter(item => item.availableForWriteOffQty > 0)
      .map(item => ({
        orderItemId: item.id,
        name: item.name,
        unitPriceCents: item.unitPriceCents,
        availableQty: item.availableForWriteOffQty,
        outstandingCents: item.outstandingCents,
        selectedQty: 0,
      })),
  );

  reason = signal('');
  submitting = signal(false);

  selectedItems = computed(() => this.lineItems().filter(item => item.selectedQty > 0));

  totalWriteOffCents = computed(() =>
    this.selectedItems().reduce((sum, item) => sum + writeOffAmountCents(item, item.selectedQty), 0),
  );

  hasSelection = computed(() => this.selectedItems().length > 0);

  canConfirm = computed(() => this.hasSelection() && this.reason().trim().length > 0);

  allSelected = computed(() => this.lineItems().every(item => item.selectedQty === item.availableQty));

  incrementQty(orderItemId: string): void {
    this.lineItems.update(items =>
      items.map(item =>
        item.orderItemId === orderItemId && item.selectedQty < item.availableQty
          ? { ...item, selectedQty: item.selectedQty + 1 }
          : item,
      ),
    );
  }

  decrementQty(orderItemId: string): void {
    this.lineItems.update(items =>
      items.map(item =>
        item.orderItemId === orderItemId && item.selectedQty > 0
          ? { ...item, selectedQty: item.selectedQty - 1 }
          : item,
      ),
    );
  }

  selectAll(): void {
    this.lineItems.update(items =>
      items.map(item => ({ ...item, selectedQty: item.availableQty })),
    );
  }

  clearAll(): void {
    this.lineItems.update(items =>
      items.map(item => ({ ...item, selectedQty: 0 })),
    );
  }

  confirm(): void {
    if (!this.canConfirm()) return;

    const items: WriteOffItemInputDto[] = this.selectedItems().map(item => ({
      orderItemId: item.orderItemId,
      qty: item.selectedQty,
    }));

    this.dialogRef.close({
      confirmed: true,
      items,
      reason: this.reason().trim(),
    });
  }

  close(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
